import React from 'react';
import { X, Play, RefreshCw, ChevronRight } from 'lucide-react';

interface PendingChanges {
  type: 'email' | 'settings';
  subject?: string;
  body?: string;
  sendWindow?: { start: string; end: string; days: string[] };
}

interface ChangeConfirmationModalProps {
  isOpen: boolean;
  pendingChanges: PendingChanges | null;
  onClose: () => void;
  onApplyChanges: (restart: boolean) => void;
}

export const ChangeConfirmationModal: React.FC<ChangeConfirmationModalProps> = ({
  isOpen,
  pendingChanges,
  onClose,
  onApplyChanges,
}) => {
  if (!isOpen || !pendingChanges) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[200] animate-fadeIn"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[400px] overflow-hidden animate-fadeInScale">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[16px] font-semibold text-neutral-900">Apply Changes</h3>
              <button
                onClick={onClose}
                className="p-1.5 -mr-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[13px] text-neutral-500">
              {pendingChanges.type === 'email' ? 'Your email template has been updated' : 'Your sending settings have been updated'}
            </p>
          </div>

          <div className="px-6 pb-6">
            <div className="space-y-2">
              <button
                onClick={() => onApplyChanges(false)}
                className="w-full flex items-center gap-4 p-4 bg-neutral-50 border-2 border-neutral-200 rounded-xl hover:border-neutral-900 hover:bg-neutral-100 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0 group-hover:border-neutral-300 transition-colors shadow-sm">
                  <Play className="w-4 h-4 text-neutral-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-[14px] font-semibold text-neutral-900">Continue campaign</p>
                    <span className="px-1.5 py-0.5 bg-neutral-900 text-white text-[9px] font-semibold uppercase tracking-wide rounded">Recommended</span>
                  </div>
                  <p className="text-[12px] text-neutral-500">
                    Apply to remaining emails only
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-900 transition-colors" />
              </button>

              <button
                onClick={() => onApplyChanges(true)}
                className="w-full flex items-center gap-4 p-4 border border-neutral-200 rounded-xl hover:border-neutral-300 hover:bg-neutral-50 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0 group-hover:bg-neutral-200 transition-colors">
                  <RefreshCw className="w-4 h-4 text-neutral-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-neutral-900 mb-0.5">Restart campaign</p>
                  <p className="text-[12px] text-neutral-500">
                    Reset stats and re-send to all
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition-colors" />
              </button>
            </div>

            <button
              onClick={onClose}
              className="w-full mt-4 py-2 text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
